import { BufferedMetricsLogger } from "datadog-metrics";
import { BaseLogServerErrorContext } from "./types";

const isDatadogEnabled =
  Boolean(process.env.DATADOG_API_KEY) ||
  process.env.JEST_WORKER_ID !== undefined;

const metrics = isDatadogEnabled
  ? new BufferedMetricsLogger({
      apiKey: process.env.DATADOG_API_KEY!,
      host: "lambda",
      prefix: "ctv.web.",
      defaultTags: [
        "type:server",
        "service:ctv-web",
        `env:${process.env.NEXT_PUBLIC_CONFIG_ENV ?? "unknown"}`,
      ],
    })
  : {
      increment: () => {},
      flush: async () => {},
    };

type LogServerErrorOptions = {
  flushImmediately?: boolean;
};

/**
 * Logs server error and optionally flushes metrics immediately.
 * This function is used to capture server-side errors and send them to Datadog for monitoring.
 * It increments an error count metric and logs the error details to the console.
 * @param {Error} err - The error object to log.
 * @param {string} [key] - An optional key to identify the error metric. Defaults to "error.count".
 * @param {LogServerErrorContext} [context] - Additional context to include with the error.
 * @param {BaseLogServerErrorOptions} [options] - Options for logging the error.
 * @param {boolean} [options.flushImmediately] - Whether to flush metrics immediately after logging the error.
 * @returns {Promise<void>} - A promise that resolves when the error is logged.
 * @example
 * --------
 * logServerError(new Error("Something went wrong"), "something.error", { userId: "12345" }, { flushImmediately: true });
 * --------
 * This will log the error to the console and increment the error count metric in Datadog.
 * The `flushImmediately` option ensures that the metrics are sent to Datadog immediately.
 * @example
 * --------
 * try {
 *  // Some code that might throw an error
 * } catch (error) {
 *  logServerError(error, "something.error", { userId: "12345" });
 * } finally {
 *  await flushMetrics(); // Ensure all metrics are flushed before the Lambda function completes
 * }
 * --------
 */
export async function logServerError(
  err: Error,
  key?: string,
  context?: BaseLogServerErrorContext,
  options?: LogServerErrorOptions
): Promise<void> {
  const tags: string[] = [];

  if (context) {
    for (const [key, value] of Object.entries(context)) {
      tags.push(`${key}:${value}`);
    }
  }

  metrics.increment(key || "error.count", 1, tags);

  console.log("[SERVER ERROR]", err.message, {
    stack: err.stack,
    ...context,
  });

  if (options?.flushImmediately) {
    await metrics.flush();
  }
}

/**
 * Use this once per Lambda execution if batching multiple metrics.
 * Flushes all buffered metrics to Datadog.
 * This is useful for ensuring that all metrics are sent before the Lambda function completes.
 * @returns {Promise<void>} - A promise that resolves when the metrics are flushed.
 * @example
 */
export async function flushMetrics(): Promise<void> {
  await metrics.flush();
}
